import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Input from "../../../../componentes/input/input";
import Button from "../../../../componentes/button/button";
import FilialService from "../../../../services/resource/filialService";
import RateioOrcamentarioEditor from "../shared/RateioOrcamentarioEditor";
import SelecionarComplementoModal from "./SelecionarComplementoModal";
import SolicitacaoCompraService from "../../../../services/resource/solicitacaoCompraService";
import { somaBateComTotal } from "../../../../utils/solicitacoes/orcamento";
import { formatarMoeda } from "../../../../utils/solicitacoes/formatters";

const service = new SolicitacaoCompraService();
const filialService = new FilialService();

/**
 * A responsabilidade pelo rateio orçamentário é do colaborador que recebe a mercadoria — é nesse
 * momento que se conhece exatamente como a despesa será distribuída. Fornecedor, tipo/descrição da
 * despesa e forma de pagamento já foram preenchidos na Etapa 6 (Ordem de Compra) — aqui só entram
 * data de recebimento, valor da nota e o rateio (cada linha já carrega seu próprio número de nota
 * fiscal, mesma validação de soma do rateio == valor total da Ordem de Compra).
 */
export default function EtapaRecebimento({ solicitacao, detalheProcesso, onAcaoConcluida, encaminhar, anexoObrigatorioNaEtapa, temAnexo, onRateioChange }) {
    const [dataRecebimento, setDataRecebimento] = useState(detalheProcesso?.dataRecebimento || "");
    const [valorTotal, setValorTotal] = useState(detalheProcesso?.valorNotaRecebida ?? "");
    const [rateio, setRateio] = useState([]);
    const [filiais, setFiliais] = useState([]);
    const [complementos, setComplementos] = useState([]);
    const [modalComplementoAberto, setModalComplementoAberto] = useState(false);
    const [enviando, setEnviando] = useState(false);

    // Parte do rateio prévio da Etapa 1 (contas BP já escolhidas pelo solicitante) para o usuário só
    // preencher valor/nota. Se a etapa já tiver um rateio gravado (ex.: voltou por retificação), usa
    // ele. `detalheProcesso` pode chegar depois do primeiro render, por isso o efeito com trava.
    const rateioInicializadoRef = useRef(false);
    useEffect(() => {
        if (rateioInicializadoRef.current || !detalheProcesso) return;
        rateioInicializadoRef.current = true;
        const rateioGravado = detalheProcesso.rateioOrcamentario || [];
        if (rateioGravado.length > 0) {
            setRateio(rateioGravado.map(item => ({ ...item, valor: item.valorRateado, numeroNotaFiscal: item.numeroNotaFiscal ?? "" })));
            return;
        }
        setRateio((detalheProcesso.rateioOrcamentarioInicial || []).map(item => ({ ...item, valor: null, numeroNotaFiscal: "" })));
    }, [detalheProcesso]);

    // Reporta o rateio em edição para a página de detalhe, que repassa para o modal de assinatura
    // de anexos — assim dá pra carimbar a tabela de rateio no PDF (nota fiscal) enquanto ainda se
    // está preenchendo esta etapa, sem precisar enviar primeiro. A página de detalhe já limpa esse
    // valor sozinha quando a etapa muda, então não precisa de limpeza aqui ao desmontar.
    useEffect(() => {
        onRateioChange?.(rateio.length > 0 ? rateio : null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rateio]);

    const valorOrdemCompra = detalheProcesso?.valorCompra ?? detalheProcesso?.valorFinalCotacao ?? null;
    const totalComplementos = complementos.reduce((soma, c) => soma + (Number(c.valorCompra) || 0), 0);

    // O rateio precisa fechar contra o valor final da Ordem de Compra da PRÓPRIA solicitação somado
    // ao das complementares (não o "valor utilizado" de cada uma, que é só informativo/rastreio) —
    // é a mesma nota fiscal cobrindo a compra de todas juntas. Sem complementos, é só o valor da
    // própria solicitação, igual ao comportamento de sempre.
    const valorFinalCompraTotal = (Number(detalheProcesso?.valorCompra) || 0)
        + complementos.reduce((soma, c) => soma + (Number(c.valorCompra) || 0), 0);

    function adicionarComplemento(item) {
        setComplementos(prev => [...prev, item]);
    }

    function removerComplemento(index) {
        setComplementos(prev => prev.filter((_, i) => i !== index));
    }

    useEffect(() => {
        filialService.listar().then(res => setFiliais(res.data)).catch(() => setFiliais([]));
    }, []);

    function validar() {
        if (!dataRecebimento || !valorTotal) {
            toast.error("Preencha todos os campos obrigatórios.");
            return false;
        }
        if (rateio.length === 0) {
            toast.error("Informe pelo menos um item de rateio.");
            return false;
        }
        if (rateio.some(item => !item.numeroNotaFiscal || !item.numeroNotaFiscal.trim())) {
            toast.error("Informe o número da nota fiscal em todos os itens do rateio.");
            return false;
        }
        const chavesRateio = rateio.map(item => `${item.baseBpId}|${item.numeroNotaFiscal.trim()}`);
        if (new Set(chavesRateio).size !== chavesRateio.length) {
            toast.error("Não é permitido repetir a mesma Conta BP para a mesma Nota Fiscal.");
            return false;
        }
        const somaRateio = rateio.reduce((soma, item) => soma + (Number(item.valor) || 0), 0);
        if (!somaBateComTotal(somaRateio, valorFinalCompraTotal)) {
            toast.error("A soma do rateio deve ser igual ao valor da compra somado ao das solicitações complementares.");
            return false;
        }
        if (anexoObrigatorioNaEtapa && !temAnexo) {
            toast.error("É necessário anexar pelo menos um documento para concluir esta etapa.");
            return false;
        }
        return true;
    }

    async function enviar() {
        if (!validar()) return;

        let usuarioEscolhidoId;
        try {
            usuarioEscolhidoId = await encaminhar("CONFIRMAR");
        } catch {
            return;
        }

        setEnviando(true);
        service.preencherRecebimento(solicitacao.id, {
            dataRecebimento,
            valorTotal: Number(valorTotal),
            rateioOrcamentario: rateio.map(item => ({ baseBpId: item.baseBpId, valor: Number(item.valor), numeroNotaFiscal: item.numeroNotaFiscal })),
            complementos: complementos.map(c => ({ solicitacaoComplementarId: c.solicitacaoComplementarId, valorUtilizado: c.valorUtilizado })),
            usuarioEscolhidoId,
        })
            .then(res => onAcaoConcluida("CONFIRMAR", res.data))
            .catch(error => console.error("Erro ao confirmar recebimento", error))
            .finally(() => setEnviando(false));
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input type="date" label="Data de recebimento" required value={dataRecebimento} onChange={e => setDataRecebimento(e.target.value)} />
                <Input type="number" label="Valor da nota" required value={valorTotal} onChange={e => setValorTotal(e.target.value)} />
            </div>

            <div>
                <label className="text-sm font-medium text-foreground/80 block mb-2">Rateio Orçamentário <span className="text-destructive">*</span></label>
                <RateioOrcamentarioEditor 
                itens={rateio}
                 onChange={setRateio}
                  filiais={filiais} 
                  comValor 
                  valorTotal={valorFinalCompraTotal} 
                  fornecedorId={detalheProcesso?.fornecedorId}
                   />
            </div>

            <div className="border border-border rounded-lg p-4 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-foreground">Solicitações Complementares</h3>
                    <Button
                        variant="outline"
                        label="Adicionar Solicitação Complementar"
                        iconLeft="fa fa-plus"
                        onClick={() => setModalComplementoAberto(true)}
                    />
                </div>

                <p className="text-xs text-muted-foreground">
                    Use quando esta nota fiscal também inclui itens de outra(s) Solicitação(ões) de Compras
                    compradas em conjunto — a solicitação complementar será finalizada automaticamente ao confirmar este recebimento.
                </p>

                {complementos.length > 0 && (
                    <>
                        <div className="overflow-x-auto border border-border rounded-lg">
                            <table className="w-full text-xs">
                                <thead className="bg-muted">
                                    <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                                        <th className="px-3 py-2 font-semibold">Nº Solicitação</th>
                                        <th className="px-3 py-2 font-semibold">Solicitante</th>
                                        <th className="px-3 py-2 font-semibold text-right">Valor</th>
                                        <th className="px-3 py-2 w-9" />
                                    </tr>
                                </thead>
                                <tbody>
                                    {complementos.map((c, index) => (
                                        <tr key={index} className="border-t border-border">
                                            <td className="px-3 py-2 text-foreground whitespace-nowrap">{c.numero}</td>
                                            <td className="px-3 py-2 text-foreground whitespace-nowrap">{c.solicitanteNome}</td>
                                            <td className="px-3 py-2 text-right text-foreground whitespace-nowrap">{formatarMoeda(c.valorUtilizado)}</td>
                                            <td className="px-3 py-2 text-center">
                                                <button
                                                    type="button"
                                                    title="Remover"
                                                    onClick={() => removerComplemento(index)}
                                                    className="h-7 w-7 inline-flex items-center justify-center rounded-full text-destructive hover:bg-destructive/10"
                                                >
                                                    <i className="fa fa-trash" />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="text-xs text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
                            <span>Valor da Ordem de Compra: <strong className="text-foreground">{valorOrdemCompra != null ? formatarMoeda(valorOrdemCompra) : "—"}</strong></span>
                            <span>Total dos complementos: <strong className="text-foreground">{formatarMoeda(totalComplementos)}</strong></span>
                            <span>Valor total considerado: <strong className="text-foreground">{formatarMoeda((Number(valorOrdemCompra) || 0) + totalComplementos)}</strong></span>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            O rateio abaixo precisa fechar com <strong className="text-foreground">{formatarMoeda(valorFinalCompraTotal)}</strong> —
                            valor da compra desta solicitação somado ao das complementares.
                        </p>
                    </>
                )}
            </div>

            <SelecionarComplementoModal
                isOpen={modalComplementoAberto}
                onClose={() => setModalComplementoAberto(false)}
                solicitacaoPrincipalId={solicitacao.id}
                idsJaAdicionados={complementos.map(c => c.solicitacaoComplementarId)}
                onSelecionar={adicionarComplemento}
            />

            <div className="flex justify-end">
                <Button variant="blue" label="Confirmar recebimento" iconLeft="fa fa-paper-plane" loading={enviando} onClick={enviar} />
            </div>
        </div>
    );
}
